import { t } from "./i18n";
import type { ConsoleConnections } from "./api";
import type { ChatCatalog } from "./chat-api";

export function validateConnectionBase(base: string): string {
  const normalized = base.trim().replace(/\/+$/, "");
  if (!normalized) throw new Error(t("Enter an API base URL, such as /api."));
  const url = new URL(normalized, window.location.href);
  if (!["http:", "https:"].includes(url.protocol) || url.username || url.password || url.search || url.hash) throw new Error(t("Use an HTTP(S) URL or relative path without credentials, query parameters or fragments."));
  if (window.location.protocol === "https:" && url.protocol === "http:") throw new Error(t("This HTTPS page cannot connect to an HTTP API. Use an HTTPS reverse proxy or a same-origin /api path."));
  return normalized;
}

/** Tests the unsaved draft, never rewrites active connections or starts agents. */
export async function testControlConnection(values: ConsoleConnections, signal: AbortSignal): Promise<ChatCatalog> {
  const base = validateConnectionBase(values.controlBase);
  if (!values.controlToken.trim()) throw new Error(t("Enter the control bearer token. A reachable server is not an authenticated connection."));
  let response: Response;
  try {
    response = await fetch(base + "/v1/chat/catalog", { headers: { Authorization: "Bearer " + values.controlToken.trim() }, signal, redirect: "error", credentials: "omit" });
  } catch (error) {
    if (signal.aborted) throw error;
    throw new Error(t("Could not reach the control API. Check the address, server listener, reverse proxy and CORS origin. 0.0.0.0 is a listener address, not the browser destination."));
  }
  if (response.status === 401 || response.status === 403) throw new Error(t("Server reachable, but authentication was rejected. Check the control bearer token (not the trace token)."));
  if (response.status === 404) throw new Error(t("Server reachable, but chat control routes were not found. Check /api proxy rewriting and that swarm api has chat enabled."));
  if (!response.ok) throw new Error("Control API returned HTTP " + response.status + ". Inspect server diagnostics before starting a session.");
  const catalog = await response.json().catch(() => null);
  if (!catalog || !Array.isArray(catalog.agents) || !Array.isArray(catalog.workers)) throw new Error(t("Response is not a chat catalog. Check the control API address and proxy configuration."));
  return catalog as ChatCatalog;
}
